import * as i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import arAuth from "./locales/ar/auth.json";
import arCommon from "./locales/ar/common.json";
import arComponents from "./locales/ar/components.json";
import arDialogs from "./locales/ar/dialogs.json";
import arErrors from "./locales/ar/errors.json";
import arForms from "./locales/ar/forms.json";
import arNavigation from "./locales/ar/navigation.json";
import arUi from "./locales/ar/ui.json";
import deAuth from "./locales/de/auth.json";
import deCommon from "./locales/de/common.json";
import deComponents from "./locales/de/components.json";
import deDialogs from "./locales/de/dialogs.json";
import deErrors from "./locales/de/errors.json";
import deForms from "./locales/de/forms.json";
import deNavigation from "./locales/de/navigation.json";
import deUi from "./locales/de/ui.json";
import enAuth from "./locales/en/auth.json";
import enCommon from "./locales/en/common.json";
import enComponents from "./locales/en/components.json";
import enDialogs from "./locales/en/dialogs.json";
import enErrors from "./locales/en/errors.json";
import enForms from "./locales/en/forms.json";
import enNavigation from "./locales/en/navigation.json";
import enUi from "./locales/en/ui.json";
import esAuth from "./locales/es/auth.json";
import esCommon from "./locales/es/common.json";
import esComponents from "./locales/es/components.json";
import esDialogs from "./locales/es/dialogs.json";
import esErrors from "./locales/es/errors.json";
import esForms from "./locales/es/forms.json";
import esNavigation from "./locales/es/navigation.json";
import esUi from "./locales/es/ui.json";
import frAuth from "./locales/fr/auth.json";
import frCommon from "./locales/fr/common.json";
import frComponents from "./locales/fr/components.json";
import frDialogs from "./locales/fr/dialogs.json";
import frErrors from "./locales/fr/errors.json";
import frForms from "./locales/fr/forms.json";
import frNavigation from "./locales/fr/navigation.json";
import frUi from "./locales/fr/ui.json";
import ptAuth from "./locales/pt/auth.json";
import ptCommon from "./locales/pt/common.json";
import ptComponents from "./locales/pt/components.json";
import ptDialogs from "./locales/pt/dialogs.json";
import ptErrors from "./locales/pt/errors.json";
import ptForms from "./locales/pt/forms.json";
import ptNavigation from "./locales/pt/navigation.json";
import ptUi from "./locales/pt/ui.json";

// Namespace import avoids import/no-named-as-default-member on instance
// methods that share names with i18next named exports (use, changeLanguage).
const i18n = i18next.default;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    // When a key is missing in the active locale, resolve from English
    // instead of rendering the dotted key path (e.g. "email.label").
    // This keeps the UI readable even when a translation is incomplete.
    returnNull: false,
    debug: import.meta.env.DEV,

    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        errors: enErrors,
        navigation: enNavigation,
        dialogs: enDialogs,
        forms: enForms,
        components: enComponents,
        ui: enUi,
      },
      es: {
        common: esCommon,
        auth: esAuth,
        errors: esErrors,
        navigation: esNavigation,
        dialogs: esDialogs,
        forms: esForms,
        components: esComponents,
        ui: esUi,
      },
      "pt-BR": {
        common: ptCommon,
        auth: ptAuth,
        errors: ptErrors,
        navigation: ptNavigation,
        dialogs: ptDialogs,
        forms: ptForms,
        components: ptComponents,
        ui: ptUi,
      },
      // "pt" alias so European Portuguese (pt-PT) browsers resolve to the
      // Portuguese bundle instead of falling back to English. i18next tries
      // the exact locale then the language-only code; without this alias,
      // navigator.language "pt" or "pt-PT" misses the "pt-BR" key and falls
      // back to fallbackLng ("en").
      pt: {
        common: ptCommon,
        auth: ptAuth,
        errors: ptErrors,
        navigation: ptNavigation,
        dialogs: ptDialogs,
        forms: ptForms,
        components: ptComponents,
        ui: ptUi,
      },
      fr: {
        common: frCommon,
        auth: frAuth,
        errors: frErrors,
        navigation: frNavigation,
        dialogs: frDialogs,
        forms: frForms,
        components: frComponents,
        ui: frUi,
      },
      de: {
        common: deCommon,
        auth: deAuth,
        errors: deErrors,
        navigation: deNavigation,
        dialogs: deDialogs,
        forms: deForms,
        components: deComponents,
        ui: deUi,
      },
      ar: {
        common: arCommon,
        auth: arAuth,
        errors: arErrors,
        navigation: arNavigation,
        dialogs: arDialogs,
        forms: arForms,
        components: arComponents,
        ui: arUi,
      },
    },

    ns: [
      "common",
      "auth",
      "errors",
      "navigation",
      "dialogs",
      "forms",
      "components",
      "ui",
    ],
    defaultNS: "common",

    interpolation: {
      escapeValue: false, // React already escapes
    },

    detection: {
      // Priority: localStorage (from a previous selection) → navigator
      // (browser language) → fallbackLng ("en"). The saved user profile
      // preference (uiLanguage from MongoDB) is applied separately after
      // login by calling i18n.changeLanguage() — see auth slice.
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
  });

/** Languages written right-to-left. Arabic is the only one we ship today. */
const RTL_LANGUAGES = new Set(["ar", "fa", "he", "ur"]);

/**
 * Resolve the writing direction for a language tag. Region subtags are
 * ignored, so "ar-EG" is still RTL.
 */
export function directionForLanguage(language?: string): "rtl" | "ltr" {
  const base = (language ?? "").toLowerCase().split("-")[0];
  return RTL_LANGUAGES.has(base) ? "rtl" : "ltr";
}

/**
 * Mirror the active language onto <html lang> and <html dir>. Without this the
 * Arabic catalog renders inside a left-to-right document: punctuation lands on
 * the wrong side, and logical CSS properties resolve backwards. Also keeps
 * `lang` accurate for screen readers and hyphenation.
 */
export function syncDocumentLanguage(language?: string): void {
  if (typeof document === "undefined") return;
  const lng = language ?? i18n.language ?? "en";
  const root = document.documentElement;
  root.setAttribute("lang", lng);
  root.setAttribute("dir", directionForLanguage(lng));
}

syncDocumentLanguage();
i18n.on("languageChanged", syncDocumentLanguage);

/**
 * Apply a language preference from the user profile, which takes priority
 * over browser detection. Call this after login or when the auth state
 * changes. If uiLanguage is undefined (no saved preference), the existing
 * detection order (localStorage → navigator → en) is left intact.
 */
export function applyProfileLanguage(uiLanguage?: string): void {
  if (!uiLanguage) return;
  if (i18n.language === uiLanguage) return;
  i18n.changeLanguage(uiLanguage);
}

export default i18n;
